import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Plus, Car, Calendar, MapPin, Star } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { TrackingBanner } from "@/components/common/TrackingBanner";

const statusMap = {
  pending: { label: "Aguardando", cls: "bg-yellow-500/15 text-yellow-300 border-yellow-500/30" },
  approved: { label: "Aprovado", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  in_progress: { label: "Em andamento", cls: "bg-sky-500/15 text-sky-300 border-sky-500/30" },
  completed: { label: "Concluído", cls: "bg-green-500/15 text-green-300 border-green-500/30" },
  canceled: { label: "Cancelado", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  diagnosis_only: { label: "Diagnóstico (não aprovado)", cls: "bg-orange-500/15 text-orange-300 border-orange-500/30" },
};

export default function Dashboard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewFor, setReviewFor] = useState(null);
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");

  useEffect(() => {
    api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));
    // Auto-refresh every 30s for live tracking
    const t = setInterval(() => {
      api.get("/bookings/mine").then((r) => setBookings(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(t);
  }, []);

  const submitReview = async () => {
    if (!comment.trim()) { toast.error("Escreva um comentário"); return; }
    try {
      await api.post("/reviews", { rating, comment, booking_id: reviewFor?.booking_id || "" });
      toast.success("Obrigado pela avaliação! Será exibida após aprovação.");
      setReviewFor(null); setComment(""); setRating(5);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar"); }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Header />
      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-12">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-sky-400 mb-3">Olá, {user?.name?.split(" ")[0]}</div>
            <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-white">Meus agendamentos</h1>
          </div>
          <Button onClick={() => nav("/agendar")} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold w-fit btn-shine" data-testid="dashboard-new-booking">
            <Plus className="w-4 h-4 mr-2" /> Novo agendamento
          </Button>
        </div>

        {loading ? (
          <div className="text-slate-400">Carregando...</div>
        ) : bookings.length === 0 ? (
          <Card className="bg-slate-900/50 border-slate-800 p-16 text-center" data-testid="dashboard-empty">
            <Calendar className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <h3 className="text-white font-bold mb-2">Nenhum agendamento ainda</h3>
            <p className="text-sm text-slate-400 mb-6">Que tal começar agora?</p>
            <Button onClick={() => nav("/agendar")} className="bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold">
              Agendar atendimento
            </Button>
          </Card>
        ) : (
          <div className="grid gap-4">
            {bookings.map((b) => {
              const st = statusMap[b.status] || statusMap.pending;
              return (
                <Card key={b.booking_id} className="bg-slate-900/50 border-slate-800" data-testid={`booking-${b.booking_id}`}>
                  <CardContent className="p-6">
                    <TrackingBanner booking={b} />
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-4">
                      <div>
                        <Badge className={`${st.cls} font-mono text-xs uppercase mb-2`}>{st.label}</Badge>
                        <h3 className="text-lg font-bold text-white">{b.car_brand} {b.car_model} {b.car_year}</h3>
                        <p className="text-sm text-slate-400 mt-1">
                          Agendado para {format(new Date(b.scheduled_date), "PPp", { locale: ptBR })}
                        </p>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-slate-500 font-mono uppercase">Total</div>
                        <div className="text-2xl font-black text-sky-400 font-mono">R$ {Number(b.total).toFixed(2)}</div>
                      </div>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2 text-sm">
                      <div className="flex items-start gap-2 text-slate-300">
                        <MapPin className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />
                        <span>{b.address}</span>
                      </div>
                      <div className="flex items-start gap-2 text-slate-300">
                        <Car className="w-4 h-4 text-sky-400 mt-0.5 shrink-0" />
                        <span>{b.problem_description}</span>
                      </div>
                    </div>
                    {b.services?.length > 0 && (
                      <div className="mt-4 pt-4 border-t border-slate-800 flex flex-wrap gap-1.5 items-center">
                        {b.services.map((s) => (
                          <Badge key={s.service_id} className="bg-slate-800 text-slate-300 border-slate-700">{s.name}</Badge>
                        ))}
                        {b.status === "completed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setReviewFor(b); setRating(5); setComment(""); }}
                            className="ml-auto border-yellow-500/40 text-yellow-300 hover:bg-yellow-500/10"
                            data-testid={`review-btn-${b.booking_id}`}
                          >
                            <Star className="w-3.5 h-3.5 mr-1" /> Avaliar atendimento
                          </Button>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </section>
      <Dialog open={!!reviewFor} onOpenChange={(o) => !o && setReviewFor(null)}>
        <DialogContent className="bg-slate-900 border-slate-700">
          <DialogHeader><DialogTitle className="text-white">Avaliar atendimento</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <div className="text-sm text-slate-300 mb-2">Sua nota</div>
              <div className="flex gap-1">
                {[1,2,3,4,5].map((n) => (
                  <button key={n} type="button" onClick={() => setRating(n)} data-testid={`star-${n}`}>
                    <Star className={`w-8 h-8 ${n <= rating ? "text-yellow-400 fill-yellow-400" : "text-slate-700"}`} />
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-sm text-slate-300 mb-2">Comentário</div>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={4} placeholder="Como foi sua experiência?" className="bg-slate-800 border-slate-700 text-white" data-testid="review-comment" />
            </div>
            <Button onClick={submitReview} className="w-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold" data-testid="review-submit">Enviar avaliação</Button>
          </div>
        </DialogContent>
      </Dialog>
      <Footer />
    </div>
  );
}
